import { Button } from "@aix/ui"
import { Book, Camera, ChefHat, Gamepad2, Guitar, Mountain, Palette, Plane, Star } from "lucide-react"
import { useState } from "react"

interface Hobby {
  id: string
  name: string
  icon: React.ReactNode
  description: string
  funFact: string
  level: "Beginner" | "Intermediate" | "Expert" | "Obsessed"
  yearsActive: number
}

const hobbies: Hobby[] = [
  {
    id: "mechanical-keyboards",
    name: "Mechanical Keyboards",
    icon: <Gamepad2 size={32} />,
    description:
      "Building custom mechanical keyboards with exotic switches and artisan keycaps. Currently rocking a 65% layout with Gateron Oil Kings.",
    funFact: "Has spent more on keycaps than on actual computer hardware",
    level: "Obsessed",
    yearsActive: 4,
  },
  {
    id: "rock-climbing",
    name: "Rock Climbing",
    icon: <Mountain size={32} />,
    description: "Bouldering indoors and sport climbing outdoors. Favorite spots include Joshua Tree and Red Rocks.",
    funFact: "Once climbed a V6 boulder problem while wearing flip-flops (accidentally)",
    level: "Intermediate",
    yearsActive: 3,
  },
  {
    id: "cooking",
    name: "Experimental Cooking",
    icon: <ChefHat size={32} />,
    description:
      "Fusion cuisine experiments combining Indian spices with Japanese techniques. Signature dish: Miso-Masala Ramen.",
    funFact: "Has a secret recipe for chai that involves exactly 7.5 cardamom pods",
    level: "Expert",
    yearsActive: 8,
  },
  {
    id: "photography",
    name: "Street Photography",
    icon: <Camera size={32} />,
    description: "Capturing urban life and architecture. Prefers shooting in black and white with a vintage Leica.",
    funFact: "Has over 50,000 unedited photos in Lightroom",
    level: "Intermediate",
    yearsActive: 6,
  },
  {
    id: "guitar",
    name: "Electric Guitar",
    icon: <Guitar size={32} />,
    description: "Playing blues and jazz on a sunburst Fender Stratocaster. Currently learning bebop scales.",
    funFact: "Can play the intro to 'Stairway to Heaven' in 3 different keys",
    level: "Intermediate",
    yearsActive: 12,
  },
  {
    id: "travel",
    name: "Adventure Travel",
    icon: <Plane size={32} />,
    description:
      "Seeking off-the-beaten-path destinations. Recent highlights: Mongolia, Bhutan, and the Faroe Islands.",
    funFact: "Has been to every continent except Antarctica (working on it)",
    level: "Expert",
    yearsActive: 15,
  },
  {
    id: "reading",
    name: "Sci-Fi & Philosophy",
    icon: <Book size={32} />,
    description:
      "Deep diving into science fiction and existential philosophy. Favorite authors: Ted Chiang, Ursula K. Le Guin, and Camus.",
    funFact: "Reads 52 books per year (one per week challenge since 2019)",
    level: "Obsessed",
    yearsActive: 20,
  },
  {
    id: "digital-art",
    name: "Digital Art & AI Art",
    icon: <Palette size={32} />,
    description:
      "Creating generative art and experimenting with AI tools. Exploring the intersection of code and creativity.",
    funFact: "Once sold an NFT of a procedurally generated taco for 0.1 ETH",
    level: "Beginner",
    yearsActive: 2,
  },
]

const levelColors: Record<Hobby["level"], string> = {
  Beginner: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  Intermediate: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  Expert: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  Obsessed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
}

export default function ShraysHobbiesScreen() {
  const [expandedHobby, setExpandedHobby] = useState<string | null>(null)
  const [showFunFacts, setShowFunFacts] = useState(false)

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">
            Shray&apos;s Hobbies
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            When not building software or drinking excessive amounts of coffee, here&apos;s what keeps me busy.
          </p>
          <div className="mt-6">
            <Button variant={showFunFacts ? "default" : "outline"} onClick={() => setShowFunFacts(!showFunFacts)}>
              <Star className="w-4 h-4 mr-2" />
              {showFunFacts ? "Hide Fun Facts" : "Show Fun Facts"}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-card rounded-xl p-4 text-center border">
            <div className="text-3xl font-bold text-primary">{hobbies.length}</div>
            <div className="text-sm text-muted-foreground">Active Hobbies</div>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border">
            <div className="text-3xl font-bold text-primary">{hobbies.reduce((acc, h) => acc + h.yearsActive, 0)}</div>
            <div className="text-sm text-muted-foreground">Combined Years</div>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border">
            <div className="text-3xl font-bold text-primary">
              {hobbies.filter((h) => h.level === "Obsessed").length}
            </div>
            <div className="text-sm text-muted-foreground">Obsessions</div>
          </div>
          <div className="bg-card rounded-xl p-4 text-center border">
            <div className="text-3xl font-bold text-primary">∞</div>
            <div className="text-sm text-muted-foreground">Unfinished Projects</div>
          </div>
        </div>

        {/* Hobbies Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {hobbies.map((hobby) => (
            <div
              key={hobby.id}
              className={`bg-card rounded-xl p-6 border transition-all duration-300 cursor-pointer hover:shadow-lg hover:border-primary/50 ${
                expandedHobby === hobby.id ? "ring-2 ring-primary" : ""
              }`}
              onClick={() => setExpandedHobby(expandedHobby === hobby.id ? null : hobby.id)}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg text-primary">{hobby.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">{hobby.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${levelColors[hobby.level]}`}>
                      {hobby.level}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{hobby.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>
                      {hobby.yearsActive} year{hobby.yearsActive !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {showFunFacts && (
                    <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                      <span className="text-amber-600 dark:text-amber-400 font-medium">Fun Fact:</span>{" "}
                      <span className="text-amber-800 dark:text-amber-200 text-sm">{hobby.funFact}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>* All hobbies subject to sudden abandonment when a new shiny thing appears</p>
          <p className="mt-2">Last updated: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  )
}
